/** The campaign: where you are in it, what the next map is, and what you take.
 *
 * A leaf. It is handed a campaign, the player's progress through it, and three
 * callbacks; it decides nothing, generates nothing and stores nothing.
 *
 * The screen is built around the one thing a campaign screen is for, which is
 * not the list: it is the *choice before the next map*. The classic puts the briefing
 * and the bonus on the same page as the map you are about to play, because
 * those three things are one decision. So the chain runs down the side as a
 * record of where you are, the middle of the screen is the next map, and the
 * choice stands on its own beside the plate that acts on it.
 *
 * ## It fills the frame, because the frame is what a player meets first
 *
 * This screen shipped as a 940px column of content-height cards hung from the
 * top of the body. Measured on its own 1920x1080 shot with the 32x32 blocks a
 * critic used across the whole build, 79.0% of the frame was flat: two small
 * panels in the top-left quadrant and three quadrants of an empty brown ramp.
 * The worst reference frame in a set of forty-nine genuine the classic captures
 * measures 11.8% by the same method, and this build's twelve town screens
 * measure 0.2-2.1%. Campaign is one of the first two screens a player ever
 * opens, so it was the cheapest and most expensive gap in the build at once.
 *
 * Three things closed it and none of them needed new art:
 *
 *  - the painting. `ScreenBackdrop` already puts a town screen behind New Game
 *    at a whole magnification with the menu's own grade on it; a campaign that
 *    stands on a horizon rather than on a gradient costs one element.
 *  - three columns that take the height. The road is a rail down the left with
 *    its legs stretched to fill it, the map is the middle, and the choice and
 *    its plate own the right - so there is no slack left to pile at the bottom.
 *  - the empty states are drawn. "Coming with you" is empty on map one, which
 *    is the state the screenshot harness always photographs, so it is ruled
 *    like the journal's empty page rather than left as a hole.
 */

import { useState } from "react";
import type { ContentRegistry } from "@game/core";

import { BitmapText } from "../theme/bitmapText.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { ScreenStyles, useMeasure } from "./primitives.js";
import { ScreenBackdrop } from "./MainMenu.js";
import { FORM_ART_CSS, PaintedPlate } from "./formArt.js";
import { groupDigits, titleCase } from "./format.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/** What the screen needs about one leg; the host flattens the file into these. */
export interface CampaignLegView {
  readonly id: string;
  readonly name: string;
  readonly briefing: string;
  /** Kingdoms on the board, including the player. */
  readonly players: number;
  readonly cells: number;
  readonly bonuses: readonly CampaignBonusView[];
  /** How many heroes this map lets the player bring with him. */
  readonly carryHeroes: number;
  readonly state: "done" | "next" | "later";
}

export interface CampaignBonusView {
  readonly label: string;
  /** A second line under the label: what it is worth, in the player's terms. */
  readonly detail: string;
}

export interface CarriedHeroView {
  readonly name: string;
  readonly level: number;
  readonly artifacts: number;
}

export interface CampaignScreenProps {
  readonly content: ContentRegistry | null;
  readonly name: string;
  readonly description: string;
  readonly legs: readonly CampaignLegView[];
  /** Heroes the next map will begin with, from the last one won. */
  readonly carried: readonly CarriedHeroView[];
  /** Index of the leg to play, or the length when the campaign is finished. */
  readonly at: number;
  readonly onBegin: (bonusIndex: number) => void;
  readonly onRestart: () => void;
  readonly onClose: () => void;
}

const LEG_TAG: Readonly<Record<CampaignLegView["state"], string>> = Object.freeze({
  done: "Won", next: "Next", later: "Later",
});

/** One figure with its unit under it, in the bitmap face the build counts in. */
function Fact(props: { readonly value: string; readonly unit: string }): JSX.Element {
  return (
    <span className="hm-cm__fact">
      <span className="hm-cm__factnum">
        <BitmapText scale={3} keyline="#1c1206">{props.value}</BitmapText>
      </span>
      <span className="hm-cm__factunit">{props.unit}</span>
    </span>
  );
}

/** A well with nothing in it yet, said in the middle of a ruled page.
 *
 * The same shape the journal uses, for the same reason: on map one nothing has
 * been carried forward and nothing ever will have been the first time anybody
 * opens this screen, so the empty state is not an edge case here - it is the
 * screen. Ruled paper reads as "nothing written yet"; a bare rectangle reads as
 * a panel that failed to load. */
function EmptyWell(props: { readonly lead: string; readonly note: string }): JSX.Element {
  return (
    <div className="hm-cart">
      <span className="hm-cart__lead">{props.lead}</span>
      <p className="hm-cart__note">{props.note}</p>
    </div>
  );
}

/**
 * The board, at the size the leg says it is.
 *
 * This is the one picture on the screen that is not decoration: the grid has
 * exactly `cells` divisions each way, so a 48x48 map and a 72x72 map are
 * visibly different boards rather than the same word in two sentences. It does
 * NOT claim to be that map's terrain - the leg view carries none, and the map
 * has not been generated when this screen is open - so what is under the grid
 * is land, the same way `ScreenBackdrop` is land, and the caption says cells
 * and tiles rather than pretending to a survey.
 */
function BoardPlate(props: { readonly cells: number; readonly art: string }): JSX.Element {
  const cells = Math.max(1, Math.round(props.cells));
  const step = `${(100 / cells).toFixed(4)}%`;
  // The square is measured, not declared. `aspect-ratio: 1` on a flex item
  // whose height comes from the flex line was tried first and did not
  // constrain the width at all - shot at 1920x1080 the plate came out 510x690
  // and drew 48 x 48 OBLONGS under a caption reading "48 x 48 cells". The box
  // is measured and the side is the smaller of the two, which is the same
  // thing said in a way the layout cannot ignore.
  const [ref, box] = useMeasure<HTMLDivElement>();
  const side = Math.max(0, Math.min(box.w, box.h));
  return (
    <div className="hm-cm__board">
      <div className="hs-card__title">The board</div>
      <div className="hm-cm__boardbox" ref={ref}>
        {side === 0 ? null : (
          <PaintedPlate
            className="hm-cm__boardart"
            atlasKey={props.art}
            focusY={0.34}
            style={{ width: `${String(side)}px`, height: `${String(side)}px` }}
          >
            <span
              className="hm-cm__grid"
              aria-hidden="true"
              style={{ backgroundSize: `${step} ${step}` }}
            />
          </PaintedPlate>
        )}
      </div>
      <div className="hm-cm__boardcap">
        {`${String(cells)} × ${String(cells)} cells · ${groupDigits(cells * cells)} tiles`}
      </div>
    </div>
  );
}

export function CampaignScreen(props: CampaignScreenProps): JSX.Element {
  usePolishStyles();
  useCampaignStyles();
  const [bonus, setBonus] = useState(0);
  const next = props.legs[props.at] ?? null;
  const finished = next === null;
  const won = props.legs.filter((l) => l.state === "done").length;

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen hm-cm ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="Campaign">
      <ScreenStyles />

      {/* The ground the road is walked over. Not information - the leg views
          carry no faction - so it is one fixed painting rather than a guess
          dressed up as one. */}
      <ScreenBackdrop faction="greenmantle" />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">{props.name}</span>
        <span className="hs-screen__sub hs-muted">
          {finished
            ? `Finished · ${String(won)} of ${String(props.legs.length)} won`
            : `Map ${String(props.at + 1)} of ${String(props.legs.length)}`}
        </span>
        <span className="hs-spacer" />
        {props.at > 0 ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            title="Back to the first map. Everything carried forward is lost."
            onClick={props.onRestart}
          >
            Start Over
          </button>
        ) : null}
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body hm-cm__body">
        {/* ------------------------------------------------------- the road */}
        <div className="hs-card hs-wood hm-cm__rail">
          <div className="hs-card__title">The road</div>
          <div className="hm-cm__railnote">{props.description}</div>
          <div className="hm-cm__legs">
            {props.legs.map((leg, i) => (
              <div className={`hm-cm__leg hm-cm__leg--${leg.state}`} key={leg.id}>
                <span className="hm-cm__legnum" aria-hidden="true">
                  {leg.state === "done"
                    ? <span className="hm-cm__legtick">✓</span>
                    : <BitmapText scale={2} keyline="#fff6d8">{String(i + 1)}</BitmapText>}
                </span>
                <span className="hm-cm__legtext">
                  <span className="hm-cm__legtop">
                    <span className="hm-cm__legname hs-clip" title={leg.name}>{leg.name}</span>
                    <span className={`hm-cm__legtag hm-cm__legtag--${leg.state}`}>
                      {LEG_TAG[leg.state]}
                    </span>
                  </span>
                  <span className="hm-cm__legmeta">
                    {`${String(leg.cells)}×${String(leg.cells)} cells · `}
                    {`${String(leg.players - 1)} rival${leg.players === 2 ? "" : "s"}`}
                  </span>
                  {/* The briefing on every leg, not only on the next one. A
                      rail of four name-and-size lines is a table of contents;
                      what a player wants from the road is to see what is ahead
                      of him, and the words are already in the leg view. */}
                  <span className="hm-cm__legbrief">{leg.briefing}</span>
                  <span className="hm-cm__legfoot">
                    {leg.bonuses.length === 0
                      ? "No opening bonus"
                      : `${String(leg.bonuses.length)} opening bonus${leg.bonuses.length === 1 ? "" : "es"}`}
                    {" · "}
                    {leg.carryHeroes === 0
                      ? "a new hero"
                      : `${String(leg.carryHeroes)} hero${leg.carryHeroes === 1 ? "" : "es"} carried in`}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* --------------------------------------------------- the next map */}
        <div className="hs-card hs-wood hm-cm__main">
          {finished ? (
            <>
              <div className="hm-cm__head">
                <span className="hm-cm__step">The road ends</span>
                <span className="hm-cm__name">{props.name}</span>
                <span className="hm-cm__rule" aria-hidden="true" />
              </div>
              <div className="hs-parch hm-cm__brief">
                Every map is behind you. Start Over puts you back at the first with
                nothing carried, which is the only way to play it again.
              </div>
              <div className="hm-cm__facts">
                <Fact value={String(props.legs.length)} unit="maps on the road" />
                <Fact value={String(won)} unit="won" />
              </div>
              <div className="hm-cm__carry">
                <div className="hs-card__title">Coming with you</div>
                <PaintedPlate
                  className="hm-cm__well"
                  atlasKey="town.greenmantle.background"
                  focusY={0.30}
                >
                  <EmptyWell
                    lead="The road is walked."
                    note="Nothing carries past the last map; Start Over begins the first again with a new hero."
                  />
                </PaintedPlate>
              </div>
            </>
          ) : (
            <>
              <div className="hm-cm__head">
                <span className="hm-cm__step">
                  {`Map ${String(props.at + 1)} of ${String(props.legs.length)}`}
                </span>
                <span className="hm-cm__name">{next.name}</span>
                <span className="hm-cm__rule" aria-hidden="true" />
              </div>

              <div className="hs-parch hm-cm__brief">{next.briefing}</div>

              <div className="hm-cm__facts">
                <Fact value={String(next.cells)} unit={`cells square (${String(next.cells * next.cells)} tiles)`} />
                <Fact
                  value={String(next.players - 1)}
                  unit={`rival kingdom${next.players === 2 ? "" : "s"}`}
                />
                <Fact
                  value={String(next.carryHeroes)}
                  unit={next.carryHeroes === 1 ? "hero carried over" : "heroes carried over"}
                />
              </div>

              {/* The middle column's own hole, closed with the thing the
                  column is about. On map one nothing is carried forward, which
                  is the state this screen is always photographed in and the
                  first state every player meets, and what stood here was a
                  700x600 ruled rectangle - the single largest empty area in the
                  build after Load Game. The board takes half of it and the
                  land takes the other half. */}
              <div className="hm-cm__spread">
              <BoardPlate cells={next.cells} art="town.ironhorde.background" />
              <div className="hm-cm__carry">
                <div className="hs-card__title">
                  {props.carried.length === 0
                    ? "Coming with you"
                    : `Coming with you (${String(props.carried.length)})`}
                </div>
                <PaintedPlate
                  className="hm-cm__well"
                  atlasKey="town.greenmantle.background"
                  focusY={0.30}
                >
                  {props.carried.length === 0 ? (
                    <EmptyWell
                      lead={next.carryHeroes === 0
                        ? "You begin this map with a new hero."
                        : "Nobody is carried into this map yet."}
                      note={"Experience, skills and what a hero was carrying come forward from a"
                        + " map you have won. Armies and movement belong to the map that"
                        + " produced them and stay there."}
                    />
                  ) : (
                    <div className="hs-list hm-cm__carrylist">
                      {props.carried.map((h) => (
                        <div className="hm-cm__hero" key={h.name}>
                          <span className="hm-cm__herolvl" aria-hidden="true">
                            <BitmapText scale={2} keyline="#fff6d8">{String(h.level)}</BitmapText>
                          </span>
                          <span className="hm-cm__herotext">
                            <span className="hm-cm__heroname hs-clip" title={h.name}>{h.name}</span>
                            <span className="hm-cm__herometa">
                              {`Level ${String(h.level)}`}
                              {h.artifacts > 0
                                ? ` · carrying ${String(h.artifacts)} artifact${h.artifacts === 1 ? "" : "s"}`
                                : " · carrying nothing"}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </PaintedPlate>
              </div>
              </div>
            </>
          )}
        </div>

        {/* ------------------------------------------------------ the choice */}
        <div className="hs-card hs-wood hm-cm__side">
          <div className="hs-card__title">{finished ? "Nothing to choose" : "Choose one"}</div>
          <div className="hm-cm__choices">
            {finished || next.bonuses.length === 0 ? (
              <PaintedPlate
                className="hm-cm__well"
                atlasKey="town.ironhorde.background"
                focusY={0.36}
              >
                <EmptyWell
                  lead={finished ? "No map is waiting." : "This map offers no opening bonus."}
                  note={finished
                    ? "Start Over, at the top of the screen, puts the first map back in front of you."
                    : "Some legs of a road are walked with what you already have."}
                />
              </PaintedPlate>
            ) : (
              next.bonuses.map((b, i) => (
                <button
                  key={b.label}
                  type="button"
                  className={`hm-cm__bonus ${i === bonus ? "hm-cm__bonus--on" : ""}`}
                  aria-pressed={i === bonus}
                  onClick={() => { setBonus(i); }}
                >
                  <span className="hm-cm__pip" aria-hidden="true" />
                  <span className="hm-cm__bonustext">
                    <span className="hm-cm__bonuslabel">{b.label}</span>
                    {b.detail === ""
                      ? null
                      : <span className="hm-cm__bonusdetail">{b.detail}</span>}
                  </span>
                </button>
              ))
            )}
          </div>
          {/* The four rules a campaign runs by, which are nowhere else in the
              shell and are exactly what a player asks when he is choosing a
              bonus he cannot change later. Also what keeps this column a
              column: two bonus plates and a button is a dialog. */}
          <div className="hm-cm__lore">
            <div className="hs-card__title">How the road runs</div>
            <p className="hm-cm__lorep">
              The bonus is taken once, when the map begins. It cannot be changed after
              Begin and it is not offered again if you come back to this map.
            </p>
            <p className="hm-cm__lorep">
              Win a map and the road moves on. Lose one and it stays where it is, so a
              leg can be fought again as many times as it takes.
            </p>
            <p className="hm-cm__lorep">
              A hero carried forward brings his experience, his skills and what he was
              carrying. His army and his movement belong to the map that raised them.
            </p>
            <p className="hm-cm__lorep">
              How far you have come is kept in this browser. Start Over clears it and
              puts the first map back in front of you.
            </p>
          </div>
          {finished ? (
            <button
              type="button"
              className="hs-plate hm-cm__begin"
              onClick={props.onRestart}
            >
              Start Over
            </button>
          ) : (
            <button
              type="button"
              className="hs-plate hm-cm__begin"
              onClick={() => { props.onBegin(bonus); }}
            >
              {`Begin ${next.name}`}
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

/** A resource bonus in the player's own terms, for `CampaignBonusView.detail`. */
export function resourceDetail(resources: Readonly<Record<string, number>>): string {
  const parts: string[] = [];
  for (const k of Object.keys(resources).sort()) {
    const n = resources[k] ?? 0;
    if (n > 0) parts.push(`${groupDigits(n)} ${titleCase(k).toLowerCase()}`);
  }
  return parts.join(", ");
}

// ------------------------------------------------------------------ styles

const CAMPAIGN_STYLE_ID = "hm-campaign-art";

/* Mounted after the kit's sheets, so equal-specificity rules here win on
   document order alone.
   NOTE: this is a template literal. No backticks below, in a comment or
   anywhere else - one ends the string early and the parse error then lands on
   a line of English prose further down the file. */
export const CAMPAIGN_CSS = `
/* Three columns, and each of them takes the height it is given. The rail is
   wide enough for a map name at 15px without an ellipsis, the choice column is
   wide enough for a bonus's second line, and the middle takes what is left -
   which at 1920 is about 800px and at 1366 about 520px. */
.hm-cm .hs-screen__body.hm-cm__body {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr) 420px;
  gap: 12px;
  padding: 12px 14px 14px;
  align-items: stretch;
}
.hm-cm__rail,
.hm-cm__main,
.hm-cm__side {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  padding: 12px 14px 14px;
}
/* The three panels are lit from the same place as every other card on this
   build, one step warmer than the shell so they sit ON the painting rather
   than in it. */
.hm-cm__rail,
.hm-cm__main,
.hm-cm__side {
  background-image:
    radial-gradient(90% 52% at 50% 0%, rgba(255,224,176,0.10) 0%, rgba(0,0,0,0) 62%),
    repeating-linear-gradient(63deg, rgba(255,226,182,0.030) 0 3px, rgba(0,0,0,0.05) 3px 7px),
    linear-gradient(178deg, #6a4e2d 0%, #553d22 48%, #3b2915 100%);
}

/* ---- the rail */
.hm-cm__railnote {
  flex: 0 0 auto;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.45;
  color: #e0caa0; /* was #c9b184, 3.5:1 */
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
  padding-bottom: 10px;
  margin-bottom: 10px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0) 0%, rgba(226,186,104,0.34) 12%, rgba(226,186,104,0.34) 88%, rgba(226,186,104,0) 100%);
  background-size: 100% 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
}
/* Four legs share the rail's height rather than stacking 56px plates at the
   top of it. A chain of four is the whole road, so there is nothing for the
   slack under it to be for. */
.hm-cm__legs {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* overflow: hidden is not tidiness. The plates share the rail's height with
   'flex: 1 1 0' and 'min-height: 0', so at 1366x768 a leg whose briefing runs
   long is given less room than its own content needs - and what a flex item
   does about that is paint over its neighbour. Shot at 768 before this, "2
   OPENING BONUSES - A NEW HERO" was printed across the words "The Crossroads"
   on the plate below it. Clipped is a layout; overlapping is a bug. The
   briefing is line-clamped at that height as well, so there is normally
   nothing left for this to clip. */
.hm-cm__leg {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 12px;
  background-image:
    linear-gradient(180deg, rgba(255,226,176,0.06), rgba(0,0,0,0.20));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.75),
    inset 0 1px 0 rgba(255,226,176,0.10),
    inset 0 -1px 0 rgba(0,0,0,0.5);
}
/* The next map is the one the rest of the screen is about, so it is the one
   the rail lights. Later maps are dimmed rather than greyed: a colour that
   only differs in saturation is not readable on a brown panel. */
.hm-cm__leg--next {
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px #d9b25a,
    inset 0 0 24px rgba(217,178,90,0.16),
    0 0 14px rgba(217,178,90,0.20);
  background-image: linear-gradient(180deg, rgba(255,226,176,0.14), rgba(0,0,0,0.16));
}
.hm-cm__leg--later { opacity: 0.62; }
.hm-cm__legnum {
  flex: 0 0 auto;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  color: #fff4d6; /* was #f6e3b2, 4.3:1 on the lit medallion (ground 125,102,64) */
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(255,244,214,0.42) 0%, rgba(255,244,214,0) 62%),
    linear-gradient(168deg, #8a6b2e 0%, #5c431a 52%, #33240b 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 1px 0 rgba(255,242,206,0.32),
    0 1px 0 rgba(0,0,0,0.6);
}
.hm-cm__leg--done .hm-cm__legnum {
  background-image:
    radial-gradient(62% 58% at 34% 24%, rgba(230,255,214,0.42) 0%, rgba(230,255,214,0) 62%),
    linear-gradient(168deg, #5d7a37 0%, #3f5522 52%, #22300f 100%);
}
.hm-cm__legtick { font-size: max(21px, var(--hm-type-floor)); line-height: 1; color: #bfe08f; }
.hm-cm__legtext {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  align-self: stretch;
  /* The plates share the rail's height equally, so a leg with a short briefing
     has slack in it. Spread rather than piled at the top: the name sits on the
     plate's head and the standing line on its foot, which is what a plate that
     was SIZED looks like instead of a plate that was stretched. */
  justify-content: space-between;
}
.hm-cm__legtop { display: flex; align-items: center; gap: 8px; min-width: 0; }
.hm-cm__legname {
  flex: 1 1 auto;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(15px, var(--hm-type-floor));
  color: #f3ddab;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
/* #c3ab80 measured 3.1:1 on the lit next-leg plate (ground 111,86,55); #ecd9ad is 4.9 there and 6.7 on a plain leg. */
.hm-cm__legmeta { font-size: max(11.5px, var(--hm-type-floor)); color: #ecd9ad; }
.hm-cm__legfoot {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(10.5px, var(--hm-type-floor));
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #c9b184; /* was #a8916a, 2.7:1 */
  padding-top: 7px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0.20) 0%, rgba(226,186,104,0.07) 72%, rgba(226,186,104,0) 100%);
  background-size: 100% 1px;
  background-position: 0 0;
  background-repeat: no-repeat;
}
.hm-cm__legbrief {
  font-size: max(12px, var(--hm-type-floor));
  line-height: 1.45;
  color: #d4bc8c; /* was #bfa87e, 4.1:1 */
  text-shadow: 0 1px 0 rgba(0,0,0,0.7);
  padding-top: 3px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0.26) 0%, rgba(226,186,104,0.10) 70%, rgba(226,186,104,0) 100%);
  background-size: 100% 1px;
  background-position: 0 0;
  background-repeat: no-repeat;
}
.hm-cm__legtag {
  flex: 0 0 auto;
  padding: 2px 7px 3px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(9.5px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #22170a;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,246,222,0.55);
}
.hm-cm__legtag--next { background-image: linear-gradient(180deg, #e6c98d, #a9863b); }
.hm-cm__legtag--done { background-image: linear-gradient(180deg, #bfe08f, #6f9a3c); }
.hm-cm__legtag--later {
  background-image: linear-gradient(180deg, rgba(120,104,74,0.6), rgba(52,42,26,0.72));
  color: #d6c49c;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}

/* ---- the map */
.hm-cm__head { flex: 0 0 auto; display: flex; flex-direction: column; gap: 3px; }
.hm-cm__step {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #dcc494; /* was #b39a6c, 2.7:1 */
}
.hm-cm__name {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: clamp(26px, 2.1vw, 40px);
  letter-spacing: 0.03em;
  color: #f7e6bb;
  text-shadow: 0 2px 0 #2c1f08, 0 3px 12px rgba(0,0,0,0.85);
}
.hm-cm__rule {
  height: 3px;
  margin-top: 6px;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0.55) 0%, rgba(226,186,104,0.30) 62%, rgba(226,186,104,0) 100%);
}
/* The briefing is the sentence the decision is made on, so it is set as prose
   on parchment rather than as a note under a heading. */
.hm-cm__brief {
  flex: 0 0 auto;
  margin: 12px 0 0;
  padding: 14px 18px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(15px, var(--hm-type-floor));
  line-height: 1.62;
}
/* Three struck figures. The build counts in the bitmap face everywhere else and
   a campaign brief full of serif digits beside it is the tell. */
.hm-cm__facts {
  flex: 0 0 auto;
  display: flex;
  gap: 10px;
  margin: 12px 0 0;
}
.hm-cm__fact {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px 8px 9px;
  text-align: center;
  background-image:
    linear-gradient(180deg, rgba(0,0,0,0.34), rgba(0,0,0,0.10));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.7),
    inset 0 1px 0 rgba(255,224,170,0.10),
    inset 0 -1px 0 rgba(255,224,170,0.06);
}
.hm-cm__factnum { color: #f0cf87; }
.hm-cm__factunit {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(10.5px, var(--hm-type-floor));
  letter-spacing: 0.13em;
  text-transform: uppercase;
  line-height: 1.35;
  color: #d4bc8c; /* was #b39a6c, 4.0:1 */
}
.hm-cm__carry {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 14px;
}
.hm-cm__carrylist { gap: 8px; }
.hm-cm__hero {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.05), rgba(0,0,0,0.16));
  box-shadow: inset 0 1px 0 rgba(255,226,176,0.09), inset 0 -1px 0 rgba(0,0,0,0.45);
}
.hm-cm__herolvl {
  flex: 0 0 auto;
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  color: #f6e3b2;
  background-image: linear-gradient(168deg, #8a6b2e 0%, #5c431a 52%, #33240b 100%);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,242,206,0.3);
}
.hm-cm__herotext { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.hm-cm__heroname {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(14px, var(--hm-type-floor));
  color: #f3ddab;
}
.hm-cm__herometa { font-size: max(12px, var(--hm-type-floor)); color: #d4bc8c; } /* was #c3ab80 */

/* ---- the choice */
.hm-cm__choices {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* background-COLOR, not only an image. A bare button in this build inherits the
   browser's own 'buttonface' grey, and a translucent gradient laid over it
   leaves the grey showing: the two bonus plates shot as flat #8b8b8b slabs, the
   two brightest objects on a brown screen, and the 32x32 blank scan counted
   every one of their blocks. Every other plate in the shell paints a colour
   under its gradient; these did not. */
/* Sized by the choice, not by the column. Stretched to share the slack the two
   plates became 400px slabs with two lines in the middle of each, which is an
   empty panel wearing a bevel. The slack goes to the rules block below. */
.hm-cm__bonus {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  color: #e6d3a6;
  border: 0;
  font: inherit;
  background-color: #4a3419;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.05), rgba(0,0,0,0.22));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.8),
    inset 0 1px 0 rgba(255,226,176,0.09);
}
.hm-cm__bonus:hover { filter: brightness(1.18) saturate(1.05); }
.hm-cm__bonus--on {
  background-color: #5c4320;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.16), rgba(0,0,0,0.14));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px #d9b25a,
    0 0 16px rgba(217,178,90,0.35);
}
/* A struck stud rather than a radio glyph: the shell has no form controls on
   it anywhere else, and a typographic circle beside a serif label is the one
   place a web form shows through. */
.hm-cm__pip {
  flex: 0 0 auto;
  width: 18px; height: 18px;
  border-radius: 50%;
  background-image:
    radial-gradient(circle at 38% 32%, rgba(255,244,214,0.30) 0 26%, rgba(0,0,0,0) 58%),
    linear-gradient(168deg, #3b2b12 0%, #201705 100%);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,236,190,0.20);
}
.hm-cm__bonus--on .hm-cm__pip {
  background-image:
    radial-gradient(circle at 38% 32%, rgba(255,255,236,0.85) 0 24%, rgba(255,255,236,0) 56%),
    linear-gradient(168deg, #ffe9a8 0%, #d9ac3b 50%, #8c6410 100%);
  box-shadow: inset 0 0 0 1px rgba(58,42,6,0.9), 0 0 12px rgba(217,178,90,0.6);
}
.hm-cm__bonustext { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.hm-cm__bonuslabel {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(15px, var(--hm-type-floor));
  color: #f3ddab;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
.hm-cm__bonusdetail { font-size: max(12.5px, var(--hm-type-floor)); line-height: 1.45; color: #d4bc8c; } /* was #c3ab80, 4.05:1 */
/* The rules block. It takes the slack in this column rather than letting two
   bonus plates stretch to 430px each - a plate whose height is four times its
   content is the same defect as an empty panel, drawn in a plate. */
.hm-cm__lore {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 14px;
  padding: 12px 14px 12px;
  background-image:
    linear-gradient(180deg, rgba(0,0,0,0.30), rgba(0,0,0,0.08));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.7),
    inset 0 1px 0 rgba(255,224,170,0.09);
}
/* Each rule is a plaque rather than a paragraph floating in a gap. The block
   takes the column's slack, and a plaque that grows is a plaque; four
   paragraphs spread by 'space-between' across 700px are four paragraphs with
   130px holes between them. */
.hm-cm__lorep {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.05), rgba(0,0,0,0.18));
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.62),
    inset 0 1px 0 rgba(255,226,176,0.08);
  margin: 0;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.52;
  color: #ccb287;
  text-shadow: 0 1px 0 rgba(0,0,0,0.75);
}
/* The plate the whole screen is pointed at, at the size that says so. */
.hm-cm__begin {
  flex: 0 0 auto;
  width: 100%;
  margin-top: 12px;
  padding: 12px 18px;
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

/* ---- the board and the well it stands beside */
.hm-cm__spread {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1fr);
  gap: 12px;
  margin-top: 14px;
}
.hm-cm__board,
.hm-cm__spread .hm-cm__carry {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  margin-top: 0;
}
/* Square, and that is not styling: the caption says 48 x 48 cells and the grid
   draws 48 divisions each way, so a box that is not square draws 48 x 48
   OBLONGS and the picture contradicts its own caption. The plate takes the
   height it is given and its width follows. */
/* The box the square is measured against. */
.hm-cm__boardbox {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hm-cm__boardart { flex: 0 0 auto; }
/* The grid IS the map size: background-size is set to 100/cells per cent in
   the component, so 48 divisions is 48 lines and 72 is 72. Two 1px gradients
   rather than a repeating one, because a repeating gradient's step is a length
   and the step here has to be a fraction of a box whose width is layout. */
.hm-cm__grid {
  position: absolute;
  inset: 0;
  z-index: 1;
  background-image:
    linear-gradient(90deg, rgba(255,232,190,0.16) 0 1px, rgba(0,0,0,0) 1px),
    linear-gradient(0deg, rgba(0,0,0,0.30) 0 1px, rgba(0,0,0,0) 1px);
  box-shadow: inset 0 0 60px rgba(0,0,0,0.55);
}
.hm-cm__boardcap {
  flex: 0 0 auto;
  padding-top: 6px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.11em;
  text-transform: uppercase;
  text-align: center;
  color: #c9b184; /* was #a8916a, 4.4:1 */
}
/* ---- an empty well: a picture with the sentence cut into it */
.hm-cm__well {
  flex: 1 1 auto;
  min-height: 0;
}
.hm-cm .hm-plate__over { padding: 14px 16px; }
@media (max-width: 1200px) {
  /* The middle column is too narrow for a board and a well side by side; the
     board is the half that carries the information, so it keeps the room. */
  .hm-cm__spread { grid-template-columns: minmax(0, 1fr); grid-template-rows: 1fr auto; }
  .hm-cm__spread .hm-cm__carry { max-height: 34%; }
}
.hm-cm__empty {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 16px 20px;
  text-align: center;
}
/* The ruling is drawn on the two spacers above and below the writing, never
   behind it: a rule pitched at 30px crossing a paragraph led at 18px strikes
   through roughly every other line, and matching the two locks a paragraph's
   leading to a decoration. A ledger with a note across it has clear paper
   where the note is. */
.hm-cm__empty::before,
.hm-cm__empty::after {
  content: "";
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
}
.hm-cm__well--ruled .hm-cm__empty::before,
.hm-cm__well--ruled .hm-cm__empty::after {
  background-image:
    repeating-linear-gradient(180deg,
      rgba(0,0,0,0) 0 30px,
      rgba(255,232,190,0.10) 30px 31px,
      rgba(0,0,0,0.16) 31px 32px);
}
.hm-cm__emptyrule {
  width: min(300px, 74%);
  height: 3px;
  flex: 0 0 auto;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0) 0%, rgba(226,186,104,0.42) 50%, rgba(226,186,104,0) 100%);
}
.hm-cm__emptylead {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(16px, var(--hm-type-floor));
  color: #d8c091;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
/* Not the #8f7c58 the journal's note started at: that measured 2.67:1 on the
   panel it is printed on, which is below the floor at any size, and this is
   the only sentence in the well. */
.hm-cm__emptynote {
  max-width: 46ch;
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.5;
  color: #ddc89e;
  text-shadow: 0 1px 0 rgba(0,0,0,0.75);
}

/* ---- 1366x768. The three columns stay three: stacking them would put the
   choice below the fold, and the choice is what the screen is for. Everything
   gives up a few points of size instead. */
@media (max-height: 880px) {
  .hm-cm .hs-screen__body.hm-cm__body {
    grid-template-columns: 270px minmax(0, 1fr) 330px;
    gap: 9px;
    padding: 9px 10px 10px;
  }
  .hm-cm__rail, .hm-cm__main, .hm-cm__side { padding: 9px 11px 11px; }
  .hm-cm__legs { gap: 7px; }
  .hm-cm__leg { padding: 7px 9px; gap: 9px; }
  .hm-cm__legnum { width: 32px; height: 32px; }
  .hm-cm__legname { font-size: max(13.5px, var(--hm-type-floor)); }
  .hm-cm__legmeta { font-size: max(11px, var(--hm-type-floor)); }
  /* Three lines of briefing and no standing line: 712px of rail over four
     plates is 149px each, and name + size + four lines + the standing line is
     more than that. The briefing is what a player reads the rail for; the
     standing line repeats what the plate beside it already says. */
  .hm-cm__legbrief {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hm-cm__legfoot { display: none; }
  .hm-cm__brief { font-size: max(13.5px, var(--hm-type-floor)); padding: 11px 14px; margin-top: 9px; }
  .hm-cm__facts { margin-top: 9px; gap: 8px; }
  .hm-cm__fact { padding: 7px 6px 6px; }
  .hm-cm__factunit { font-size: max(9.5px, var(--hm-type-floor)); letter-spacing: 0.10em; }
  .hm-cm__carry { margin-top: 10px; }
  .hm-cm__bonus { padding: 9px 11px; }
  .hm-cm__bonuslabel { font-size: max(14px, var(--hm-type-floor)); }
  .hm-cm__bonusdetail { font-size: max(11.5px, var(--hm-type-floor)); }
  .hm-cm__begin { padding: 9px 14px; font-size: max(13.5px, var(--hm-type-floor)); margin-top: 9px; }
  .hm-cm__emptylead { font-size: max(14px, var(--hm-type-floor)); }
  .hm-cm__emptynote { font-size: max(11.5px, var(--hm-type-floor)); }
}
/* The road card's ground (114,86,52) is lighter than the kit's: the shared gold heading measured 4.2:1. */
.hm-cm .hs-card__title { color: #f8e6b0; }
`;

const CAMPAIGN_SHEET = `${FORM_ART_CSS}\n${CAMPAIGN_CSS}`;

function useCampaignStyles(): void {
  useStyleSheet(CAMPAIGN_STYLE_ID, CAMPAIGN_SHEET, StyleLayer.screen);
}
